import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, MessageSquare, CalendarCheck, ShieldCheck, Clock, ClipboardCheck,
  SlidersHorizontal, ChevronLeft, FileText, Phone, Mail, MapPin, CheckCircle2
} from 'lucide-react';
import './CommercialServices.css';

const FEATURES = [
  {
    icon: MessageSquare,
    title: 'Consistent Communication',
    desc: 'Your own client portal where you can message us, make requests, reschedule, and stay informed.'
  },
  {
    icon: CalendarCheck,
    title: 'Reliable Scheduling',
    desc: 'Your business is placed on a repeating schedule so you\'re never missed and never forgotten.'
  },
  {
    icon: ShieldCheck,
    title: 'Professional Staff',
    desc: 'Uniformed, trained, background-checked team members who take pride in representing your business well.'
  },
  {
    icon: Clock,
    title: 'Flexible Hours',
    desc: 'Daytime or after-hours options so cleaning never disrupts your workflow.'
  },
  {
    icon: ClipboardCheck,
    title: 'Quality Assurance',
    desc: 'Management reviews service logs, supports your team, and ensures your workspace stays up to standard.'
  },
  {
    icon: SlidersHorizontal,
    title: 'Customizable Service',
    desc: 'From small offices to large commercial spaces, we tailor every schedule and scope to meet your needs.'
  }
];

const PROPERTY_TYPES = [
  'Offices',
  'Retail Spaces',
  'Real Estate & Staging',
  'Small Businesses',
  'Medical & Dental Offices',
  'Restaurants & Food Service'
];

const SERVICE_MODEL = [
  'Initial deep clean to establish a baseline for your space',
  'Recurring scheduled service with locked-in appointment slots',
  'Assigned team lead overseeing each account',
  'Consistent team assignment across every visit',
  'Add-on services available (carpet cleaning, appliance detailing, etc.)',
  'Recurring clients receive discounted rates on specialty services'
];

function CommercialServices() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  React.useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className={`commercial-page ${isVisible ? 'visible' : ''}`}>
      <div className="commercial-container">
        {/* Back nav */}
        <button className="commercial-back" onClick={() => navigate('/')}>
          <ChevronLeft size={18} />
          Back to Keel Group
        </button>

        {/* Hero */}
        <div className="commercial-hero">
          <div className="commercial-hero-icon">
            <Building2 size={36} />
          </div>
          <h1>Keel Commercial Services</h1>
          <p className="commercial-hero-tagline">
            Reliable, professional cleaning for offices, retail spaces, real estate,
            and small businesses throughout Charlottesville.
          </p>
        </div>

        {/* Property Types */}
        <div className="commercial-section">
          <h2>Spaces We Serve</h2>
          <div className="commercial-property-types">
            {PROPERTY_TYPES.map(type => (
              <div key={type} className="property-type-chip">
                <CheckCircle2 size={16} />
                {type}
              </div>
            ))}
          </div>
        </div>

        {/* Feature Cards */}
        <div className="commercial-section">
          <h2>Why Businesses Choose Us</h2>
          <div className="commercial-features">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="feature-card">
                <div className="feature-card-icon">
                  <Icon size={24} />
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div className="commercial-section">
          <h2>How It Works</h2>
          <div className="commercial-service-model">
            {SERVICE_MODEL.map((step, i) => (
              <div key={i} className="service-model-step">
                <div className="step-number">{i + 1}</div>
                <p>{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="commercial-cta">
          <h2>Ready to get started?</h2>
          <p>Every commercial space is unique. Let us build a custom cleaning plan for your business.</p>
          <a href="https://keelgroup.app/quote" className="commercial-quote-btn">
            <FileText size={18} />
            Request a Quote
          </a>
        </div>

        {/* Contact */}
        <div className="commercial-contact">
          <a href="tel:+14342072246">
            <Phone size={16} />
            (434) 207-2246
          </a>
          <a href="mailto:sales@twosisterscleaningco.com">
            <Mail size={16} />
            sales@twosisterscleaningco.com
          </a>
          <span>
            <MapPin size={16} />
            Charlottesville, VA
          </span>
        </div>

        <p className="commercial-copyright">
          © {new Date().getFullYear()} Keel Group
        </p>
      </div>
    </div>
  );
}

export default CommercialServices;
